import { useEffect, useState } from 'react';
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Progress,
  Table,
  Alert,
  Paper,
  RingProgress,
  Center,
} from '@mantine/core';
import {
  IconServer,
  IconDeviceDesktop,
  IconPhoto,
  IconUsers,
  IconPlayerPlay,
  IconClock,
  IconAlertCircle,
  IconCloud,
  IconCloudOff,
  IconRefresh,
} from '@tabler/icons-react';
import { useFogDashboard, type FogTask } from '@/hooks';
import { FogLoadingOverlay } from '@/components/fog';

interface StatCardProps {
  title: string;
  value: number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  delay?: number;
}

function StatCard({ title, value, subtitle, icon, color, delay = 0 }: StatCardProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  // Animate number counting up
  useEffect(() => {
    if (!isVisible) return;
    const duration = 500;
    const steps = 20;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [value, isVisible]);

  return (
    <Card
      withBorder
      p="md"
      radius="md"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
        transition: 'all 400ms cubic-bezier(0.16, 1, 0.3, 1)',
        cursor: 'pointer',
      }}
      className="stat-card"
    >
      <Group justify="space-between">
        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            {title}
          </Text>
          <Text fw={700} size="xl" className="number-animate">
            {displayValue}
          </Text>
          {subtitle && (
            <Text size="xs" c="dimmed">
              {subtitle}
            </Text>
          )}
        </div>
        <ThemeIcon
          color={color}
          variant="light"
          size={48}
          radius="md"
          style={{
            transition: 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
          className="icon-container"
        >
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );
}

function getTaskStateBadge(stateID: number) {
  switch (stateID) {
    case 1:
      return <Badge color="yellow" size="sm">Queued</Badge>;
    case 2:
      return <Badge color="blue" size="sm">Checked In</Badge>;
    case 3:
      return <Badge color="cyan" size="sm">In Progress</Badge>;
    case 4:
      return <Badge color="green" size="sm">Complete</Badge>;
    case 5:
      return <Badge color="red" size="sm">Cancelled</Badge>;
    default:
      return <Badge color="gray" size="sm">Unknown</Badge>;
  }
}

function getTaskTypeName(typeID: number) {
  switch (typeID) {
    case 1:
      return 'Deploy';
    case 2:
      return 'Capture';
    case 8:
      return 'Multicast';
    default:
      return `Type ${typeID}`;
  }
}

function TasksTable({ tasks }: { tasks: FogTask[] }) {
  const [visibleRows, setVisibleRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    tasks.forEach((_, index) => {
      setTimeout(() => {
        setVisibleRows((prev) => new Set([...prev, index]));
      }, index * 80);
    });
  }, [tasks]);

  if (tasks.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="md">
        No active tasks
      </Text>
    );
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Host</Table.Th>
          <Table.Th>Type</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Progress</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {tasks.map((task, index) => (
          <Table.Tr
            key={task.id}
            style={{
              opacity: visibleRows.has(index) ? 1 : 0,
              transform: visibleRows.has(index) ? 'translateX(0)' : 'translateX(-10px)',
              transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
              cursor: 'pointer',
            }}
            className="table-row-hover"
          >
            <Table.Td>{task.hostName || `Host #${task.hostID}`}</Table.Td>
            <Table.Td>{task.typeName || getTaskTypeName(task.typeID)}</Table.Td>
            <Table.Td>{getTaskStateBadge(task.stateID)}</Table.Td>
            <Table.Td style={{ width: 150 }}>
              {task.percent !== undefined ? (
                <Progress
                  value={task.percent}
                  size="sm"
                  animated={task.stateID === 3}
                  className="progress-animated"
                />
              ) : (
                <Text size="xs" c="dimmed">-</Text>
              )}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

export function FogDashboardPage() {
  const { data, isLoading, error, isRefetching } = useFogDashboard();

  if (isLoading) {
    return (
      <Container size="xl" py="md">
        <FogLoadingOverlay message="Loading FOG Dashboard..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load FOG dashboard: {error.message}
        </Alert>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} title="No Data" color="yellow">
          No data received from FOG server.
        </Alert>
      </Container>
    );
  }

  const stats = data;

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>FOG Dashboard</Title>
          <Text c="dimmed" size="sm">
            Real-time FOG Project statistics
          </Text>
        </div>
        <Badge
          size="lg"
          variant="light"
          color={stats.connected ? 'green' : 'red'}
          leftSection={stats.connected ? <IconCloud size={14} /> : <IconCloudOff size={14} />}
          rightSection={isRefetching ? <IconRefresh size={12} className="icon-spin" /> : null}
          className={stats.connected ? 'connected-glow' : ''}
          style={{
            transition: 'all 300ms ease',
          }}
        >
          {stats.connected ? 'Connected' : 'Disconnected'}
        </Badge>
      </Group>

      {!stats.connected ? (
        <Alert icon={<IconCloudOff size={16} />} title="FOG Server Offline" color="red" mb="lg">
          Cannot connect to FOG server. Make sure the FOG VM is running:
          <Text size="sm" mt="xs" ff="monospace">
            cd vagrant/fog-server && vagrant up
          </Text>
        </Alert>
      ) : null}

      {/* Stats Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" mb="lg">
        <StatCard
          title="Hosts"
          value={stats.hosts.total}
          subtitle={stats.hosts.pending > 0 ? `${stats.hosts.pending} pending` : undefined}
          icon={<IconDeviceDesktop size={24} />}
          color="blue"
          delay={0}
        />
        <StatCard
          title="Groups"
          value={stats.groups.total}
          icon={<IconUsers size={24} />}
          color="violet"
          delay={100}
        />
        <StatCard
          title="Images"
          value={stats.images.total}
          icon={<IconPhoto size={24} />}
          color="teal"
          delay={200}
        />
        <StatCard
          title="Active Tasks"
          value={stats.tasks.active}
          subtitle={stats.tasks.queued > 0 ? `${stats.tasks.queued} queued` : undefined}
          icon={<IconPlayerPlay size={24} />}
          color="orange"
          delay={300}
        />
      </SimpleGrid>

      {/* Server Info + Tasks */}
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        {/* Server Info Card */}
        <Card withBorder radius="md">
          <Card.Section withBorder inheritPadding py="xs">
            <Group justify="space-between">
              <Text fw={500}>FOG Server</Text>
              <ThemeIcon color="gray" variant="light" size="sm">
                <IconServer size={14} />
              </ThemeIcon>
            </Group>
          </Card.Section>

          <Stack gap="sm" mt="md">
            {stats.server ? (
              <>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Version</Text>
                  <Badge variant="light">{stats.server.version}</Badge>
                </Group>
                {stats.server.hostname && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Hostname</Text>
                    <Text size="sm">{stats.server.hostname}</Text>
                  </Group>
                )}
                {stats.server.kernel && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Kernel</Text>
                    <Text size="sm" ff="monospace">{stats.server.kernel}</Text>
                  </Group>
                )}
                {stats.server.uptime && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Uptime</Text>
                    <Text size="sm">{stats.server.uptime}</Text>
                  </Group>
                )}
              </>
            ) : (
              <Text c="dimmed" ta="center" py="md">
                Server info unavailable
              </Text>
            )}
          </Stack>
        </Card>

        {/* Task Activity Card */}
        <Card withBorder radius="md">
          <Card.Section withBorder inheritPadding py="xs">
            <Group justify="space-between">
              <Text fw={500}>Task Activity</Text>
              <Group gap="xs">
                {stats.tasks.active > 0 && (
                  <Badge color="cyan" size="sm">{stats.tasks.active} active</Badge>
                )}
                {stats.tasks.queued > 0 && (
                  <Badge color="yellow" size="sm">{stats.tasks.queued} queued</Badge>
                )}
              </Group>
            </Group>
          </Card.Section>

          <Card.Section>
            <TasksTable tasks={stats.tasks.recent} />
          </Card.Section>
        </Card>
      </SimpleGrid>

      {/* Quick Stats Ring */}
      {stats.connected && (
        <Paper withBorder p="md" mt="lg" radius="md">
          <Text fw={500} mb="md">System Overview</Text>
          <Group justify="center" gap={50}>
            <Stack align="center" gap="xs">
              <RingProgress
                size={80}
                thickness={8}
                sections={[{ value: stats.hosts.total > 0 ? 100 : 0, color: 'blue' }]}
                label={
                  <Center>
                    <IconDeviceDesktop size={20} />
                  </Center>
                }
              />
              <Text size="xs" c="dimmed">Hosts</Text>
            </Stack>
            <Stack align="center" gap="xs">
              <RingProgress
                size={80}
                thickness={8}
                sections={[{ value: stats.groups.total > 0 ? 100 : 0, color: 'violet' }]}
                label={
                  <Center>
                    <IconUsers size={20} />
                  </Center>
                }
              />
              <Text size="xs" c="dimmed">Groups</Text>
            </Stack>
            <Stack align="center" gap="xs">
              <RingProgress
                size={80}
                thickness={8}
                sections={[{ value: stats.images.total > 0 ? 100 : 0, color: 'teal' }]}
                label={
                  <Center>
                    <IconPhoto size={20} />
                  </Center>
                }
              />
              <Text size="xs" c="dimmed">Images</Text>
            </Stack>
            <Stack align="center" gap="xs">
              <RingProgress
                size={80}
                thickness={8}
                sections={[
                  { value: stats.tasks.active > 0 ? 60 : 0, color: 'cyan' },
                  { value: stats.tasks.queued > 0 ? 40 : 0, color: 'yellow' },
                ]}
                label={
                  <Center>
                    <IconClock size={20} />
                  </Center>
                }
              />
              <Text size="xs" c="dimmed">Tasks</Text>
            </Stack>
          </Group>
        </Paper>
      )}
    </Container>
  );
}
